import { Input, Select, Switch, Table } from '@dreamtree-org/twreact-ui';
import { SKIP, mappedKeys, needsGrade, sharedConditions } from '../lib/shopifyCsv';

/**
 * The mapping step of the Shopify import (MERCH-90): each Shopify type onto one of the store's types (or skipped),
 * one condition for the batch, the grade column when that condition needs one, each mapped type's required specs
 * (a default, or a metafield column per product) and the store's own fields, and whether to import images. It edits
 * the form only; the dry run (ShopifyImport) checks it on the server.
 */
function SpecInput({ attr, value, onChange }) {
  if (attr.options || attr.kind === 'boolean') {
    const options = attr.options ? attr.options.map((o) => ({ value: o, label: o })) : [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
    return <Select label={attr.label} options={options} value={value || ''} onChange={(v) => onChange(v || '')} closeOnSelect required={attr.required} />;
  }
  return <Input label={attr.label} type={attr.kind === 'number' ? 'number' : 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} required={attr.required} />;
}

/** A column → key map edited one key at a time: `key`'s column (or none). */
const columnFor = (cols, key) => Object.keys(cols).find((c) => cols[c] === key) || '';
const withColumn = (cols, key, column) => {
  const next = Object.fromEntries(Object.entries(cols).filter(([, k]) => k !== key));
  if (column) next[column] = key;
  return next;
};

/**
 * One mapped type: its required specs (a default for every product, or a metafield column whose value overrides it
 * per product) and the store's own fields.
 */
function TypeMapping({ type, form, columns, metafields, setForm }) {
  const specs = (form.attributes || {})[type.key] || {};
  const specCols = (form.specs || {})[type.key] || {};
  const cols = (form.fields || {})[type.key] || {};
  const setSpecColumn = (key, column) => setForm((f) => ({ ...f, specs: { ...f.specs, [type.key]: withColumn(specCols, key, column) } }));
  const setSpec = (key, v) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [type.key]: { ...specs, [key]: v } } }));
  const setField = (fieldKey, column) => setForm((f) => ({ ...f, fields: { ...f.fields, [type.key]: withColumn(cols, fieldKey, column) } }));
  const required = type.attributes.filter((a) => a.required);
  return (
    <div className="space-y-2 rounded-lg border border-[var(--app-border)] p-3">
      <p className="text-sm font-medium">{type.plural}</p>
      {required.length ? <p className="text-xs opacity-70">Every product imported as {type.label.toLowerCase()} gets these specs (edit any product later).</p> : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {required.map((a) => (
          <div key={a.key} className="space-y-1">
            <SpecInput attr={a} value={specs[a.key]} onChange={(v) => setSpec(a.key, v)} />
            {metafields.length ? (
              <Select
                label={`${a.label} from a column (optional)`}
                options={[{ value: '', label: 'Use the value above for all' }, ...metafields.map((c) => ({ value: c, label: c }))]}
                value={columnFor(specCols, a.key)}
                onChange={(v) => setSpecColumn(a.key, v)}
                helperText="A product's own value in this column replaces the one above."
                closeOnSelect
              />
            ) : null}
          </div>
        ))}
        {type.custom_fields.map((field) => (
          <Select
            key={field.key}
            label={`Your field: ${field.label}`}
            options={[{ value: '', label: 'Not imported' }, ...columns.map((c) => ({ value: c, label: c }))]}
            value={columnFor(cols, field.key)}
            onChange={(v) => setField(field.key, v)}
            closeOnSelect
          />
        ))}
      </div>
    </div>
  );
}

export default function ShopifyMapping({ report, form, setForm }) {
  const types = report.types.map((t) => ({ value: t.key, label: t.plural }));
  const conditions = sharedConditions(report, form);
  const mapped = report.types.filter((t) => mappedKeys(form).includes(t.key));
  return (
    <>
      <Table
        data={report.source_types.map((s, i) => ({ id: i, ...s }))}
        columns={[
          { key: 'source', label: 'Shopify type', render: (s) => s.source || '(no type)' },
          { key: 'counts', label: 'Products / lines', render: (s) => `${s.products} / ${s.rows}` },
          {
            key: 'map', label: 'Import as', wrap: true,
            render: (s) => (
              <Select
                options={[{ value: SKIP, label: 'Skip these products' }, ...types]}
                value={form.types[s.source] || SKIP}
                onChange={(v) => setForm((f) => ({ ...f, types: { ...f.types, [s.source]: v || SKIP } }))}
                helperText={s.suggested ? `Suggested: ${(report.types.find((t) => t.key === s.suggested) || {}).plural}` : 'No suggestion'}
                closeOnSelect
              />
            ),
          },
        ]}
        showSerial={false}
        withAction={false}
        showColumnPicker={false}
        showReloadButton={false}
        wrapCells
      />
      {mapped.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Condition of everything in this file"
            options={conditions.map((c) => ({ value: c.key, label: c.label }))}
            value={form.condition}
            onChange={(v) => setForm((f) => ({ ...f, condition: v || f.condition }))}
            helperText="One condition per import. Everything imports as a draft."
            closeOnSelect
          />
          {needsGrade(report, form) ? (
            <Select
              label="Column with the grade (A–D)"
              options={[{ value: '', label: 'None' }, ...report.columns.grade_candidates.map((c) => ({ value: c, label: c }))]}
              value={form.grade_column}
              onChange={(v) => setForm((f) => ({ ...f, grade_column: v || '' }))}
              helperText="Without one, these products are refused."
              closeOnSelect
            />
          ) : null}
        </div>
      ) : null}
      {mapped.map((t) => <TypeMapping key={t.key} type={t} form={form} columns={report.columns.mappable} metafields={report.columns.metafields} setForm={setForm} />)}
      <Switch checked={form.images} onChange={(on) => setForm((f) => ({ ...f, images: on }))} rightLabel={`Import product images from ${report.images.host} (${report.images.importable})`} />
    </>
  );
}
